<main class="fixed inset-0 flex overflow-hidden font-sans">

  <!-- Left: Brand Panel -->
  <aside class="hidden lg:flex lg:w-[44%] flex-col flex-shrink-0 relative overflow-hidden bg-primary-800">

    <!-- Subtle dot grid -->
    <div class="absolute inset-0"
      style="background-image: radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px); background-size: 28px 28px;"></div>

    <!-- Bottom-right glow -->
    <div class="absolute bottom-0 right-0 w-[500px] h-[500px] -mb-32 -mr-32 rounded-full"
      style="background: radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 65%);"></div>

    <div class="relative z-10 flex flex-col h-full px-14 py-12">

      <!-- Logo -->
      <div class="flex items-center gap-3">
        <img src="icons/direct-logo-footer.png" alt="Direct Savings & Loans" class="h-10 w-auto"/>
        <p class="text-[9px] font-semibold uppercase tracking-[0.22em] text-white/50">Back Office</p>
      </div>

      <!-- Main copy — vertically centred -->
      <div class="flex-1 flex flex-col justify-center">
        <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-white/35 mb-5">Loan Management System</p>
        <h1 class="text-[2.6rem] font-bold text-white leading-[1.15] tracking-tight mb-5">
          One platform.<br/>Total portfolio<br/>control.
        </h1>
        <p class="text-[13px] text-white/45 leading-relaxed max-w-[300px]">
          Manage institutions, process loan applications, and track repayments — all from a single secure workspace.
        </p>

        <!-- Divider -->
        <div class="w-10 h-0.5 bg-white dark:bg-slate-800/20 my-8 rounded-full"></div>

        <!-- Feature list -->
        <div class="space-y-3">
          @for (item of [
            'Institution & member onboarding',
            'Loan lifecycle management',
            'Repayment tracking & analytics',
            'Role-based access control'
          ]; track $index) {
          <div class="flex items-center gap-3">
            <div class="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 bg-white dark:bg-slate-800/10">
              <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <span class="text-[12px] text-white/55">{{ item }}</span>
          </div>
          }
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between pt-5 border-t border-white/10">
        <div class="flex items-center gap-2">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse block"></span>
          <span class="text-[9px] font-semibold uppercase tracking-widest text-white/25">Systems Operational</span>
        </div>
        <p class="text-[9px] text-white/20">© {{ currentYear }} DSL</p>
      </div>

    </div>
  </aside>

  <!-- Right: Form Panel -->
  <section class="flex-1 flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-8 overflow-y-auto">
    <div class="w-full max-w-[360px]">

      <!-- Mobile logo -->
      <div class="flex items-center gap-3 mb-10 lg:hidden bg-primary-800 px-4 py-2.5 rounded-xl w-fit">
        <img src="icons/direct-logo-footer.png" alt="Direct Savings & Loans" class="h-8 w-auto"/>
        <p class="text-[10px] text-white/50 font-semibold uppercase tracking-widest">Back Office</p>
      </div>

      <!-- Heading -->
      <div class="mb-8">
        <p class="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mb-2">Secure Sign In</p>
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Welcome back</h2>
        <p class="text-[13px] text-slate-500 dark:text-slate-400 mt-1.5">Enter your credentials to continue.</p>
      </div>

      <!-- Form -->
      <form [formGroup]="loginData" (ngSubmit)="loginUser()" class="space-y-5">

        <!-- Email -->
        <div>
          <label class="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
            Email Address
          </label>
          <app-primary-input
            [type]="'email'"
            [placeholder]="'admin@directsl.com'"
            [showLabel]="false"
            [image]="_images.personIcon"
            formControlName="email">
          </app-primary-input>
          @if (loginData.get('email')?.invalid && loginData.get('email')?.touched) {
          <p class="text-error text-[11px] font-medium mt-1">Please enter a valid email address.</p>
          }
        </div>

        <!-- Password -->
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <label class="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Password</label>
            <button type="button" (click)="goToForgotPassword()"
              class="text-[11px] font-semibold text-primary-800 dark:text-primary-400 hover:underline underline-offset-2 transition-colors">
              Forgot password?
            </button>
          </div>
          <app-primary-input
            [type]="showPassword ? 'text' : 'password'"
            [placeholder]="'Enter your password'"
            [showLabel]="false"
            [image]="!showPassword ? _images.unvisible : _images.visible"
            formControlName="password"
            (onViewEvent)="togglePasswordVissibility()">
          </app-primary-input>
          @if (loginData.get('password')?.invalid && loginData.get('password')?.touched) {
          <p class="text-error text-[11px] font-medium mt-1">Password is required (min 6 characters).</p>
          }
        </div>

        <!-- Submit -->
        <div class="pt-1">
          <button type="submit" [disabled]="isLoading"
            class="w-full h-11 flex items-center justify-center gap-2 text-sm font-semibold text-white rounded-lg bg-primary-800 hover:bg-primary-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            @if (isLoading) {
            <span class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <span>Signing in...</span>
            } @else {
            <span>Sign In</span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
            }
          </button>
        </div>

      </form>

      <!-- Footer -->
      <div class="mt-8 flex items-center justify-between pt-6 border-t border-slate-200 dark:border-slate-700">
        <div class="flex items-center gap-1.5">
          <svg class="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
          <p class="text-[11px] text-slate-400">Authorised personnel only</p>
        </div>
        <a href="mailto:support@directsl.com"
          class="text-[11px] font-semibold text-primary-800 dark:text-primary-400 hover:underline underline-offset-2 transition-colors">
          IT Support
        </a>
      </div>

    </div>
  </section>

</main>
